import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Loader2, ShieldCheck } from 'lucide-react';
import { authAPI } from '../services/api';
import { useAuth } from '../utils/AuthContext';

export default function PlatformAdminLoginPage() {
  const navigate = useNavigate();
  const { login, isAuthenticated, isPlatformAdmin } = useAuth();

  const [step, setStep] = useState('credentials');
  const [loginChallenge, setLoginChallenge] = useState('');
  const [form, setForm] = useState({
    email: '',
    password: '',
    otp: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAuthenticated && isPlatformAdmin) {
      navigate('/platform-admin/onboarding-requests');
    }
  }, [isAuthenticated, isPlatformAdmin, navigate]);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const submitCredentials = async (e) => {
    e.preventDefault();
    setError('');
    setLoginChallenge('');
    setLoading(true);

    try {
      const res = await authAPI.loginInit({
        email: form.email,
        password: form.password,
      });

      setLoginChallenge(res.data.login_challenge);
      setStep('otp');
    } catch (err) {
      setError(err?.response?.data?.error || 'Invalid platform admin credentials.');
    } finally {
      setLoading(false);
    }
  };

  const submitOTP = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await authAPI.loginVerify({
        login_challenge: loginChallenge,
        otp: form.otp,
      });

      const loggedInUser = await login(res.data.user, res.data.tokens);

      if (
        loggedInUser?.role !== 'PLATFORM_ADMIN' &&
        loggedInUser?.is_superuser !== true &&
        loggedInUser?.is_staff !== true
      ) {
        localStorage.clear();
        setError('This account is not authorized for the Platform Admin Portal.');
        setLoginChallenge('');
        setStep('credentials');
        return;
      }

      navigate('/platform-admin/onboarding-requests');
    } catch (err) {
      setError(err?.response?.data?.error || 'OTP verification failed.');
    } finally {
      setLoading(false);
    }
  };

  const resendOTP = async () => {
    if (!loginChallenge) return;

    setError('');
    setLoading(true);

    try {
      const res = await authAPI.resendOTP(loginChallenge);
      if (res.data?.login_challenge) setLoginChallenge(res.data.login_challenge);
    } catch (err) {
      setError(err?.response?.data?.error || 'Failed to resend OTP.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-8">
      <div className="mx-auto max-w-md">
        <button
          type="button"
          onClick={() => navigate('/')}
          className="mb-6 inline-flex items-center gap-2 text-sm font-semibold text-slate-600 hover:text-slate-950"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to landing
        </button>

        <div className="rounded-3xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 text-white shadow-sm">
              <ShieldCheck className="h-7 w-7" />
            </div>

            <h1 className="text-2xl font-bold text-slate-950">
              Platform Admin Portal
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Review and approve PII Platform onboarding requests.
            </p>
          </div>

          {error && (
            <div className="mb-5 flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {step === 'credentials' ? (
            <form onSubmit={submitCredentials} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Email
                </label>
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  required
                />
              </div>

              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  Password
                </label>
                <input
                  type="password"
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-3 font-semibold text-white hover:bg-orange-700 disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Send OTP
              </button>
            </form>
          ) : (
            <form onSubmit={submitOTP} className="space-y-4">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-slate-700">
                  OTP
                </label>
                <input
                  type="text"
                  name="otp"
                  value={form.otp}
                  onChange={handleChange}
                  className="w-full rounded-xl border border-slate-300 px-4 py-3 outline-none focus:border-orange-500 focus:ring-2 focus:ring-orange-100"
                  required
                />
                <p className="mt-1 text-xs text-slate-500">
                  Enter the OTP sent to your platform admin email.
                </p>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-orange-600 px-4 py-3 font-semibold text-white hover:bg-orange-700 disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Verify and continue
              </button>

              <button
                type="button"
                onClick={resendOTP}
                disabled={loading}
                className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
              >
                Resend OTP
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}